/**
 * API for all Reports-related things:
 * Schedule Reports, etc.
 */

import { URLS } from '@/constants';
import { Report, ReportDownloadType, SigmaReport } from '@/models/reports';
import { useUserStore } from '@/piniaStores/user/user.store';
import session from '@/services/session';

import { SigmaRequestPayload } from './models/sigmaReports';

/**
 * Fetches all ScheduleReport for a given Company Id.
 * @param {string} companyId The company to fetch Brands from
 */
export const getScheduleReports = () => session.get(`${URLS.REPORT.SCHEDULE}/`);

/**
 * Fetches all Report types.
 */
export const getReportTypes = () => session.get(`${URLS.REPORT.TYPES}/`);

/**
 * Adds a new Schedule Report.
 * @param {object} data The Schedule Report containing all relevant data.
 */
export const addScheduleReport = (data: Report) => {
  return session.post<Report>(`${URLS.REPORT.SCHEDULE}/`, data);
};

/**
 * Removes a Schedule Report.
 * @param {object} data The Schedule Report to delete.
 */
export const removeScheduleReport = (data: Report) => session.delete(`${URLS.REPORT.SCHEDULE}/${data.id}/`);

/**
 * Downloads reports of a company
 * @param {number} [data.company] for which company to download the reports
 * @param {string} [data.reportType] of the report to download
 */
export async function downloadReports(data: ReportDownloadType) {
  return await session.post(`${URLS.REPORT.DOWNLOAD}/?${URLS.CUSTOM_ERROR}`, data);
}

/**
 * Get Tableau Report for Replenishment Suggestions
 * @param {object} args - the function arguments, collected in this object
 * @param {string} args.reportName - the unique kebab-base name of the report
 * @param {string} args.tableauUsername - optional argument
 * @returns {Promise<string>} the report url
 */
export async function getInsightsReportUrl(args: { reportName: string; tableauUsername: string }): Promise<string> {
  const { reportName = '', tableauUsername = '' } = args;

  const userStore = useUserStore();

  const params = {
    report_name: reportName,
    company_slug: userStore.userActiveCompany?.slug,
  };

  if (tableauUsername) {
    params['tableau_username'] = tableauUsername;
  }

  let reportUrl = '';

  const response = await session.get(`${URLS.INSIGHTS.BASE}/?`, { params });

  reportUrl = response.data;

  return reportUrl;
}

/**
 * Fetches the signed URL for the requested Sigma report
 * @param payload The request body containing the report type, and any params to include
 */
export const getSigmaReport = async (payload: SigmaRequestPayload) => {
  const response = await session.post<SigmaReport>(URLS.INTEGRATIONS.SIGMA, payload);

  return response.data;
};
